<template>
  <div class="w-full bg-background px-6 py-3">
    <div
      class="flex flex-row items-center justify-start space-x-2"
    >
      <div class="flex flex-col justify-center">
        <p
          class="line-clamp-1 w-9 text-left text-sm font-semibold"
        >
          {{ formattedTime }}
        </p>
      </div>

      <div class="flex flex-1 justify-start">
        <span
          class="mr-2 line-clamp-1 text-left text-xs text-background-fg/70"
        >
          {{
            [numberOfItemsString, order.note]
              .filter(Boolean)
              .join(" - ")
          }}
        </span>
      </div>

      <PaymentDetailChip
        v-if="!isCashiersEnabled && order.invoice?.paidBy"
        :payment="order.invoice.paidBy"
      />

      <p
        class="min-w-12 text-right font-semibold text-background-fg"
      >
        {{ formattedTotalCost }}
      </p>
    </div>

    <!-- Negative left margin (-ml-1.5) aligns the cashier pill with the order time text for visual consistency -->
    <div
      v-if="
        isCashiersEnabled &&
        !isCashierLoading &&
        !isCashierError
      "
      class="-ml-1.5 mt-0.5 flex flex-row items-center justify-between"
    >
      <span
        class="inline-block rounded-full bg-background-fg/25 px-2 py-px text-xs text-white"
        :style="{
          backgroundColor:
            cashier?.color ?? 'rgb(var(--color-cool-grey))',
        }"
      >
        {{ cashier?.username || "No Cashier" }}
      </span>

      <PaymentDetailChip
        v-if="order.invoice?.paidBy"
        :payment="order.invoice.paidBy"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
const { isProductsEnabled } = useTenant()
import type { Order } from "@/services/orders/listOrders"
import type { TransactionsFeedOrder } from "@/services/orders/listTransactionsFeed"
import { useCashiers } from "@/services/cashiers/listCashiers"

const { formatToMerchantCurrencyString } = useCurrency()
const {
  getById,
  isError: isCashierError,
  isLoading: isCashierLoading,
} = useCashiers()

const props = defineProps<{
  order: Order | TransactionsFeedOrder
}>()

const { isCashiersEnabled } = useTenant()

const cashier = computed(
  () => getById(props.order.cashierId).value
)

const formattedTime = computed(() =>
  formatToTime(
    props.order.invoice?.paidAt ?? props.order.createdAt
  )
)
const formattedTotalCost = computed(() =>
  formatToMerchantCurrencyString(props.order.totalCost)
)

const numberOfItemsString = computed(() => {
  const count = props.order.numberOfItems
  if (count === 0) return ""
  if (count === 1)
    return isProductsEnabled.value ? "1 item" : ""
  return `${count} items`
})
</script>
